import type { ReactNode } from 'react'
import './RecordCards.css'

/**
 * The list-screen card, for phones.
 *
 * Every list screen got the same treatment in the design — a bold name, a
 * status pill on the right, and one or two lines of the fields that identify
 * the record. The designer's round-four note settles it: "the same card recipe
 * (title + status badge + two meta lines) … all five list screens now converge
 * on one pattern."
 *
 * Screens no longer render this themselves. `DataTable` owns the breakpoint and
 * maps its rows onto these cards, so a list gets its phone layout from the same
 * place it gets its table — see `DataTable`'s card mode.
 *
 * The publishing plan keeps its own (`MobilePostCards`): it carries channel
 * dots and a time, and it shows a near-term window rather than the whole list.
 */

export interface RecordCard {
  id: string
  title: string
  /** A logo or avatar beside the title — the same node the table row leads with. */
  leading?: ReactNode
  /** Under the title, smaller — location, kind · country, that sort of thing. */
  subtitle?: string
  status?: { label: string; bg: string; color: string }
  /** One or two lines under the rule. Falsy entries are dropped. */
  lines: (string | false | null | undefined)[]
  onOpen: () => void
}

export function RecordCards({ cards, empty }: { cards: RecordCard[]; empty: ReactNode }) {
  if (cards.length === 0) return <div className="empty-note">{empty}</div>

  return (
    <div className="reccards">
      {cards.map((c) => (
        <button
          className="reccard"
          key={c.id}
          onClick={(e) => {
            // The desktop row does this and the card did not, which is why a
            // lead could not be opened on a phone at all: the leads pane closes
            // its drawer on any click that reaches it, so the tap opened the
            // drawer and the same event closed it again.
            e.stopPropagation()
            c.onOpen()
          }}
        >
          <div className="reccard__top">
            <div className="reccard__id">
              {c.leading}
              <div>
              <div className="reccard__title">{c.title}</div>
              {c.subtitle && <div className="reccard__sub">{c.subtitle}</div>}
              </div>
            </div>
            {c.status && (
              <span
                className="pill reccard__pill"
                style={{ background: c.status.bg, color: c.status.color }}
              >
                {c.status.label}
              </span>
            )}
          </div>
          {c.lines.filter(Boolean).map((line, i) => (
            <div className="reccard__line" key={i}>
              {line}
            </div>
          ))}
        </button>
      ))}
    </div>
  )
}
